import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  STATUS_COLORS,
  STATUS_LABEL_KEYS,
  STATUS_PLACEHOLDER_KEY,
  STATUS_VALUES,
} from "./rsvpConstants";
import "./AttendanceSelector.css";

// ─── Chevron icon ──────────────────────────────────────────────

const Chevron = () => (
  <svg
    className="rsvp-attn-chevron"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M2.5 4.5L6 8L9.5 4.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ─── Component ─────────────────────────────────────────────────

/**
 * AttendanceSelector
 *
 * A keyboard-accessible custom combobox for picking a per-member,
 * per-section attendance status. Controlled via `value` / `onChange`.
 *
 * Props:
 *   value        — one of STATUS_VALUES or "pending"
 *   onChange     — called with the next status (always a STATUS_VALUES entry)
 *   memberName   — used in the composed aria-label for screen readers
 *   sectionName  — used in the composed aria-label for screen readers
 *   disabled     — disables the trigger
 */
const AttendanceSelector = ({
  value,
  onChange,
  memberName,
  sectionName,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const listboxId = useId();
  const optionIdPrefix = useId();

  const currentIndex = STATUS_VALUES.indexOf(value);

  const openPanel = useCallback(() => {
    if (disabled) return;
    setHighlightIndex(currentIndex >= 0 ? currentIndex : 0);
    setIsOpen(true);
  }, [disabled, currentIndex]);

  const closePanel = useCallback(
    ({ restoreFocus = false } = {}) => {
      setIsOpen(false);
      if (restoreFocus && triggerRef.current) {
        triggerRef.current.focus();
      }
    },
    [],
  );

  const commit = useCallback(
    (index) => {
      const next = STATUS_VALUES[index];
      if (!next) return;
      onChange?.(next);
      closePanel({ restoreFocus: true });
    },
    [onChange, closePanel],
  );

  // Click-outside closes.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(e.target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [isOpen]);

  // Move focus to the panel when it opens so arrow-key navigation works.
  useEffect(() => {
    if (isOpen && panelRef.current) {
      panelRef.current.focus({ preventScroll: true });
    }
  }, [isOpen]);

  // Keep the highlighted option visible (scrollIntoView if panel scrolls).
  useEffect(() => {
    if (!isOpen) return;
    const panel = panelRef.current;
    if (!panel) return;
    const optionEl = panel.querySelector(
      `[data-index="${highlightIndex}"]`,
    );
    if (optionEl && typeof optionEl.scrollIntoView === "function") {
      optionEl.scrollIntoView({ block: "nearest" });
    }
  }, [isOpen, highlightIndex]);

  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    switch (e.key) {
      case "ArrowDown":
      case "ArrowUp":
      case "Enter":
      case " ":
        e.preventDefault();
        openPanel();
        break;
      default:
        break;
    }
  };

  const handlePanelKeyDown = (e) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightIndex((i) => (i + 1) % STATUS_VALUES.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightIndex(
          (i) => (i - 1 + STATUS_VALUES.length) % STATUS_VALUES.length,
        );
        break;
      case "Home":
        e.preventDefault();
        setHighlightIndex(0);
        break;
      case "End":
        e.preventDefault();
        setHighlightIndex(STATUS_VALUES.length - 1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        commit(highlightIndex);
        break;
      case "Tab":
        // Commit the highlighted value and let Tab proceed naturally.
        commit(highlightIndex);
        break;
      case "Escape":
        e.preventDefault();
        e.stopPropagation(); // don't bubble to RsvpModal's ESC handler
        closePanel({ restoreFocus: true });
        break;
      default:
        break;
    }
  };

  const displayValue = value && STATUS_LABEL_KEYS[value] ? value : "pending";
  const colors = STATUS_COLORS[displayValue] || STATUS_COLORS.pending;
  const label =
    displayValue === "pending"
      ? t(STATUS_PLACEHOLDER_KEY)
      : t(STATUS_LABEL_KEYS[displayValue]);
  const ariaSection = t("rsvp.attendance.ariaSection");
  const ariaStatus =
    displayValue === "pending"
      ? t("rsvp.status.ariaNotSelected")
      : t(STATUS_LABEL_KEYS[displayValue]);
  const ariaLabel = `${memberName ? memberName + " — " : ""}${
    sectionName ? sectionName + " " + ariaSection : ariaSection
  }: ${ariaStatus}`;

  return (
    <div
      ref={rootRef}
      className="rsvp-attn-root"
      data-open={isOpen ? "true" : "false"}
    >
      <button
        ref={triggerRef}
        type="button"
        className="rsvp-attn-trigger"
        disabled={disabled}
        onClick={() => (isOpen ? closePanel() : openPanel())}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-label={ariaLabel}
        style={{
          background: colors.bg,
          border: `1px solid ${colors.border}`,
        }}
      >
        <span className="rsvp-attn-label">{label}</span>
        <Chevron />
      </button>
      <ul
        ref={panelRef}
        id={listboxId}
        role="listbox"
        tabIndex={-1}
        className={`rsvp-attn-panel${isOpen ? " is-open" : ""}`}
        aria-activedescendant={
          isOpen ? `${optionIdPrefix}-${highlightIndex}` : undefined
        }
        onKeyDown={handlePanelKeyDown}
      >
        {STATUS_VALUES.map((status, idx) => {
          const selected = value === status;
          const highlighted = isOpen && idx === highlightIndex;
          return (
            <li
              key={status}
              id={`${optionIdPrefix}-${idx}`}
              role="option"
              aria-selected={selected}
              data-index={idx}
              data-highlighted={highlighted ? "true" : "false"}
              className="rsvp-attn-option"
              onMouseEnter={() => setHighlightIndex(idx)}
              onMouseDown={(e) => {
                // Prevent the panel losing focus before we commit.
                e.preventDefault();
                commit(idx);
              }}
            >
              <span>{t(STATUS_LABEL_KEYS[status])}</span>
              <span
                className="rsvp-attn-option-dot"
                style={{ background: STATUS_COLORS[status].dot }}
                aria-hidden="true"
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default AttendanceSelector;
